import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Shield } from 'lucide-react';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password)
      return setError('Enter email and password');
    setLoading(true);
    try {
      const { data, error: authErr } =
        await supabase.auth.signInWithPassword({
          email: email.trim(), password,
        });
      if (authErr) throw authErr;

      const uid = data.user?.id;

      // Verify admin
      const { data: adminData } = await supabase
        .from('admin_users')
        .select('id')
        .eq('auth_user_id', uid)
        .maybeSingle();

      if (!adminData) {
        await supabase.auth.signOut();
        throw new Error('Access denied. This portal is for admins only.');
      }

      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg,#0F172A 0%,#1E293B 50%,#0F172A 100%)',
      display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: 24,
    }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 60, height: 60,
            background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
            borderRadius: 16, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 12px',
            boxShadow: '0 8px 32px rgba(79,142,247,0.4)',
          }}>
            <Shield size={28} color="#fff" />
          </div>
          <div style={{
            fontWeight: 800, fontSize: 22,
            background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: 1,
          }}>UNIVERSE Admin</div>
          <div style={{
            color: '#475569', fontSize: 13, marginTop: 4,
          }}>Restricted Access — Authorized Personnel Only</div>
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(20px)',
          borderRadius: 20, padding: 32,
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}>
          {error && (
            <div style={{
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 10, padding: '10px 14px',
              marginBottom: 20, display: 'flex',
              alignItems: 'center', gap: 8,
              color: '#FCA5A5', fontSize: 13,
            }}>
              <AlertCircle size={15} />{error}
            </div>
          )}

          <form onSubmit={handleLogin}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{
                display: 'block', fontSize: 12,
                fontWeight: 600, color: '#94A3B8', marginBottom: 6,
              }}>Admin Email</label>
              <div style={{ position: 'relative' }}>
                <Mail size={15} style={{
                  position: 'absolute', left: 12,
                  top: '50%', transform: 'translateY(-50%)',
                  color: '#475569',
                }} />
                <input type="email" value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="admin@inncatechnologies.com"
                  style={{
                    width: '100%',
                    padding: '11px 14px 11px 36px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 10, fontSize: 14,
                    color: '#F1F5F9',
                    background: 'rgba(255,255,255,0.05)',
                    outline: 'none',
                  }}
                  onFocus={e => e.target.style.borderColor = '#4F8EF7'}
                  onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                />
              </div>
            </div>

            <div>
              <label style={{
                display: 'block', fontSize: 12,
                fontWeight: 600, color: '#94A3B8', marginBottom: 6,
              }}>Password</label>
              <div style={{ position: 'relative' }}>
                <Lock size={15} style={{
                  position: 'absolute', left: 12,
                  top: '50%', transform: 'translateY(-50%)',
                  color: '#475569',
                }} />
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••••"
                  style={{
                    width: '100%',
                    padding: '11px 40px 11px 36px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 10, fontSize: 14,
                    color: '#F1F5F9',
                    background: 'rgba(255,255,255,0.05)',
                    outline: 'none',
                  }}
                  onFocus={e => e.target.style.borderColor = '#4F8EF7'}
                  onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                />
                <button type="button"
                  onClick={() => setShowPass(!showPass)}
                  style={{
                    position: 'absolute', right: 12,
                    top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none',
                    cursor: 'pointer', color: '#475569',
                    display: 'flex', alignItems: 'center',
                  }}>
                  {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading}
              style={{
                width: '100%', padding: '13px',
                border: 'none', borderRadius: 12,
                background: loading ? '#334155'
                  : 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                color: '#fff', fontWeight: 700, fontSize: 14,
                cursor: loading ? 'not-allowed' : 'pointer',
                marginTop: 4,
                boxShadow: loading ? 'none'
                  : '0 4px 16px rgba(79,142,247,0.4)',
                display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: 8,
              }}>
              {loading ? (
                <div style={{
                  width: 18, height: 18,
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: '#fff',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
              ) : (
                <><Shield size={15} />Access Admin Panel</>
              )}
            </button>
          </form>
        </div>

        <p style={{
          textAlign: 'center', fontSize: 11,
          color: '#334155', marginTop: 16,
        }}>
          © 2025 Innca Technologies · Admin Portal v1.0
        </p>
      </div>
    </div>
  );
}